/** 定时任务和IM设置共用平台白名单，避免前后端各自维护可通知平台。 */
export const IM_NOTIFICATION_PLATFORMS = ['dingtalk', 'feishu', 'telegram', 'discord', 'nim', 'wechatwork', 'qq', 'wechat'] as const;
export type IMNotificationPlatform = typeof IM_NOTIFICATION_PLATFORMS[number];

/** 浏览器只持有服务端生成的目标ID；不接收会话令牌或第三方回复上下文。 */
export interface IMNotificationTarget {
  id: string;
  label: string;
  chatType: 'direct' | 'group';
  lastSeenAt: number;
}
/** 每个实例/平台仅有一个显式目标；失败不改变任务本身的执行结果。 */
export interface IMNotificationSettings {
  targets: IMNotificationTarget[];
  selectedId: string | null;
  lastDelivery: { code: 'sent' | 'failed' | 'not_selected'; at: number } | null;
}

/** 解析不可信通知快照；拒绝缺失/越界/自相矛盾的数据，只返回公开字段。 */
export function parseIMNotificationSettings(value: unknown): IMNotificationSettings {
  const source = notificationRecord(value);
  if (!source || !Array.isArray(source.targets) || source.targets.length > 100) return invalidNotificationSettings();
  const ids = new Set<string>();
  const targets = source.targets.map((raw): IMNotificationTarget => {
    const target = notificationRecord(raw);
    if (!target || typeof target.id !== 'string' || !target.id || target.id.length > 128 || ids.has(target.id)
      || typeof target.label !== 'string' || !target.label || target.label.length > 100
      || (target.chatType !== 'direct' && target.chatType !== 'group') || !notificationTimestamp(target.lastSeenAt)) {
      return invalidNotificationSettings();
    }
    ids.add(target.id);
    return { id: target.id, label: target.label, chatType: target.chatType, lastSeenAt: target.lastSeenAt };
  });
  const selectedId = source.selectedId === null ? null : typeof source.selectedId === 'string' ? source.selectedId : invalidNotificationSettings();
  if (selectedId !== null && !ids.has(selectedId)) return invalidNotificationSettings();
  let lastDelivery: IMNotificationSettings['lastDelivery'] = null;
  if (source.lastDelivery !== null) {
    const delivery = notificationRecord(source.lastDelivery);
    if (!delivery || (delivery.code !== 'sent' && delivery.code !== 'failed' && delivery.code !== 'not_selected')
      || !notificationTimestamp(delivery.at)) return invalidNotificationSettings();
    lastDelivery = { code: delivery.code, at: delivery.at };
  }
  return { targets, selectedId, lastDelivery };
}

/** 仅接受 JSON 对象；数组或空值不能作为业务快照。 */
function notificationRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

/** 时间使用非负安全整数毫秒，禁止 NaN/Infinity 导致页面格式化失败。 */
function notificationTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/** 错误仅包含固定诊断，不把远端返回内容或凭据带到页面。 */
function invalidNotificationSettings(): never {
  throw new Error('Invalid IM notification settings');
}
